import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkle, TrendUp, UsersThree } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";

const AUTH_BG = "https://images.unsplash.com/photo-1557682260-96773eb01377?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHx3YXJtJTIwYWVzdGhldGljJTIwb3JhbmdlJTIwZ3JhZGllbnQlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODY4ODc3MjJ8MA&ixlib=rb-4.1.0&q=85&w=1400";

export default function AuthPage({ initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", invite_code: "" });
  const [inviteRequired, setInviteRequired] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setInviteRequired(!!data.invite_required)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data);
      navigate(mode === "register" ? "/onboarding" : "/", { replace: true });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF9F6] font-figtree">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D2422]/80 via-[#2D2422]/30 to-transparent" />
        <Link to="/" className="relative w-fit"><BrandMark size={34} dark dot="#FFE9A8" /></Link>
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FFD8C4] mb-4">AI-powered ads command center</p>
          <h1 className="font-outfit text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Your ads.<br />
            <span className="text-[#FFD8C4]">On autopilot.</span>
          </h1>
          <p className="mt-6 text-white/85 text-base leading-relaxed max-w-md">
            Connect your Facebook ad accounts, and a friendly AI team designs creatives, optimizes budgets and hunts quality leads — you just approve.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-4">
          {[
            { icon: TrendUp, label: "Decisions", value: "Scale / Pause" },
            { icon: Sparkle, label: "AI Engine", value: "Claude Opus 4.7" },
            { icon: UsersThree, label: "Focus", value: "Quality Leads" },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-xl p-4">
              <Icon size={18} weight="duotone" className="text-[#FFD8C4]" />
              <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">{label}</div>
              <div className="text-sm font-semibold text-white mt-0.5">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-block mb-8"><BrandMark size={28} /></Link>
          <h2 className="font-outfit text-3xl font-extrabold tracking-tight text-[#2D2422]">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-sm text-[#8a7f76] mt-2">
            {mode === "login" ? "Sign in to your ads command center." : "Create your workspace — your own AI ads team in minutes."}
          </p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            {mode === "register" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Full name</Label>
                  <Input id="name" data-testid="register-name-input" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nayan Sharma" className="h-11 rounded-xl bg-white border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Business / workspace name</Label>
                  <Input id="company" data-testid="register-company-input" value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder="Acme Institute" className="h-11 rounded-xl bg-white border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
                </div>
                {inviteRequired && (
                  <div className="space-y-1.5">
                    <Label htmlFor="invite" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Invite code</Label>
                    <Input id="invite" data-testid="register-invite-input" required value={form.invite_code}
                      onChange={(e) => setForm({ ...form, invite_code: e.target.value })}
                      placeholder="Your invite code" className="h-11 rounded-xl bg-white border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
                  </div>
                )}
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com" className="h-11 rounded-xl bg-white border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a7f76]">Password</Label>
              <Input id="password" data-testid="auth-password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••" className="h-11 rounded-xl bg-white border-[#E5DCD0] focus-visible:ring-[#FF6B35]" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit-button"
              className="w-full h-11 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm transition-transform hover:-translate-y-0.5 active:scale-[0.98] shadow-[0_8px_30px_rgba(255,107,53,0.25)]">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-[#E5DCD0]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#b3a99e]">or</span>
            <div className="h-px flex-1 bg-[#E5DCD0]" />
          </div>
          <button type="button" data-testid="google-login-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-11 rounded-full border border-[#E5DCD0] bg-white hover:border-[#FF6B35]/50 text-[#2D2422] font-bold text-sm flex items-center justify-center gap-2.5 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>
          <p className="mt-6 text-sm text-[#8a7f76]">
            {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-mode"
              onClick={() => {
                const next = mode === "login" ? "register" : "login";
                setMode(next);
                setError("");
                navigate(next === "register" ? "/signup" : "/login", { replace: true });
              }}
              className="font-bold text-[#FF6B35] hover:underline">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
